<template>
  <div class="i-adzuna-job-board">
    <div
      v-for="(job, index) in props.jobListings"
      :key="job.id"
      class="listing"
      @click="openUrl(job.url, index + 1)"
    >
      <div class="company">{{ job.company }}</div>
      <div class="title">
        <span class="name">{{ job.title }}</span>
        <span
          v-if="['Today', 'Yesterday'].includes(daysAgo(job.created))"
          class="new"
          >New</span
        >
      </div>
      <div class="deets">
        <div class="salary">{{ job.salaryRange }}</div>
        <div class="loc-day">
          <div class="location">{{ job.location }}</div>
          <div class="day">{{ daysAgo(job.created) }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import GtmService from "@/services/gtmService"

// TYPES
import { JobListing } from "@/shared/dtos/out/JobGetListingsOut"

//PROPS
const props = defineProps({
  jobListings: {
    type: Array as () => JobListing[],
    required: true
  },
  jobId: {
    type: Number,
    required: true
  }
})

// FUNCTIONS
const openUrl = async (adzunaUrl: string, positionInList: number) => {
  const adClickDetails = {
    adPlatform: "adzuna",
    adFormat: "jobListing",
    linkUrl: adzunaUrl,
    jobId: props.jobId,
    positionInList
  }

  try {
    await GtmService.adzunaClickEvent(adClickDetails)
  } catch (error) {
    console.error("Error reporting adzuna ad click to Google Tag Manager", error)
  }

  window.open(adzunaUrl, "_blank")
}

const daysAgo = (date: Date) => {
  const now = new Date()
  const diffInMs = now.getTime() - new Date(date).getTime()
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))

  return diffInDays == 0 ? "Today" : diffInDays == 1 ? "Yesterday" : `${diffInDays} days ago`
}
</script>

<style lang="scss" scoped>
.i-adzuna-job-board {
  @include border-radius-lg;
  @include flex(column, start, stretch, $gap: 16px);

  border: 1px solid $zumthor100;
  padding: 24px;
  margin: 25px 0;
  max-height: 480px;
  height: 100%;
  overflow: auto;

  &::-webkit-scrollbar {
    height: 5px;
    width: 5px;
    background: $black50;
  }

  &::-webkit-scrollbar-thumb {
    background: $black200;
    border-radius: 10px;
  }

  .listing {
    @include border-radius-md;
    @include flex(column, start, stretch, $gap: 2px);
    @include text-xs;

    border: 1px solid $zumthor100;
    padding: 14px;
    color: $black500;
    font-weight: 600;
    cursor: pointer;

    @media screen and (min-width: $breakpoint-xs) {
      @include text-sm;

      padding: 18px;
    }

    &:hover {
      background-color: $azure-radiance50;
    }

    .title {
      @include text-sm;

      font-weight: 700;
      color: $comet800;

      @media screen and (min-width: $breakpoint-xs) {
        @include text-md;
      }

      .name {
        margin-right: 10px;
      }

      .new {
        @include border-radius-lg;
        @include text-xs;

        width: fit-content;
        font-weight: 500;
        color: $white;
        background-color: $azure-radiance600;
        padding: 1px 8px;
      }
    }

    .deets {
      @include flex(column, start, stretch, $gap: 3px);

      margin-top: 3px;

      @media screen and (min-width: $breakpoint-sm) {
        @include flex(row, start, center, $gap: 10px);
      }

      .salary {
        flex: 0 0 max-content;
      }

      .loc-day {
        @include flex(column, start, stretch, $gap: 3px);

        width: 100%;

        @media screen and (min-width: $breakpoint-xs) {
          @include flex(row, space-between, center);
        }

        .location {
          @media screen and (min-width: $breakpoint-sm) {
            padding-left: 10px;
            border-left: 2px solid $black500;
          }
        }

        .day {
          @include text-xs;

          font-weight: 400;
        }
      }
    }
  }
}
</style>
